import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonNotificationService } from 'src/app/common/service/common-notification.service';
import { AuthSessionService } from 'src/app/providers/auth-session/auth-session.service';
import { environment } from 'src/environments/environment';

/**
 * Sign-in page. Two ways in, both ending in the same HttpOnly session cookie:
 *  - SQL:  user name + password posted to the API (with the CSRF header).
 *  - SSO:  "Sign in with Microsoft" redirects to the API, which runs the Entra
 *          sign-in and sends the browser back here; ngOnInit then loads the session.
 */
@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {
  email = '';
  password = '';
  showPassword = false;
  isSubmitting = false;
  isRedirectingToSso = false;
  /** True while checking for an existing session (page refresh / SSO return). */
  isCheckingSession = true;
  errorMessage: string | null = null;
  /** MFA step: shown after the password is accepted for users with MFA on. */
  pinStep = false;
  pin = '';
  pinEmail: string | null = null;
  readonly ssoEnabled = environment.ssoEnabled !== false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private _authSession: AuthSessionService,
    private _commonNotificationService: CommonNotificationService) {
  }

  async ngOnInit() {
    // The API sends SSO failures back as /?ssoError=<message>.
    const ssoError = this.route.snapshot.queryParamMap.get('ssoError');
    if (ssoError) {
      // Shown as plain text (Angular escapes it); trimmed so the URL can't carry a long message.
      this.errorMessage = ssoError.substring(0, 200);
      this.isCheckingSession = false;
      this.router.navigate([], { queryParams: {}, replaceUrl: true });
      await this._authSession.ensureCsrfToken();
      return;
    }

    // Returning from SSO, or refreshing with a live cookie: pick up the session.
    const session = await this._authSession.restoreSession();
    if (session.ok) {
      this._commonNotificationService.displaySuccessMessage("Loading environment. . .");
      await this._authSession.navigateToHome(session.user);
      return;
    }
    if (session.message) {
      // Signed in with Microsoft but not linked to a Cereus user.
      this.errorMessage = session.message;
      await this._authSession.logout({ stayOnPage: true });
    }
    this.isCheckingSession = false;
    await this._authSession.ensureCsrfToken();
  }

  /** Toggles the password field between masked and plain text. */
  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  /** SQL (email / password) sign-in. */
  async onSignIn() {
    this.errorMessage = null;
    const userName = this.email.trim();
    if (!userName || !this.password) {
      this.errorMessage = 'Please enter both email and password.';
      return;
    }

    this.isSubmitting = true;
    try {
      const result = await this._authSession.loginWithPassword(userName, this.password);
      if (result.mfaRequired) {
        this.password = '';
        this.pinStep = true;
        this.pinEmail = result.mfaEmail ?? null;
        return;
      }
      if (!result.ok) {
        this.errorMessage = result.message || 'Invalid user name or password.';
        return;
      }
      this.password = '';
      this._commonNotificationService.displaySuccessMessage("Loading environment. . .");

      await this._authSession.navigateToHome(result.user);
      // this._authSession.navigateToHomeNew(result.user);

    } finally {
      this.isSubmitting = false;
    }
  }

  /** MFA: submit the PIN that was e-mailed. */
  async onVerifyPin() {
    this.errorMessage = null;
    const pin = this.pin.trim();
    if (!/^\d{6}$/.test(pin)) {
      this.errorMessage = 'Please enter the 6-digit PIN.';
      return;
    }
    this.isSubmitting = true;
    try {
      const result = await this._authSession.verifyPin(pin);
      if (!result.ok) {
        this.errorMessage = result.message || 'Invalid PIN.';
        this.pin = '';
        return;
      }
      this._commonNotificationService.displaySuccessMessage("Loading environment. . .");
      await this._authSession.navigateToHome(result.user);
    } finally {
      this.isSubmitting = false;
    }
  }

  /** Leave the PIN step and start over. */
  cancelPin(): void {
    this.pinStep = false;
    this.pin = '';
    this.errorMessage = null;
  }

  /** "Sign in with Microsoft": hands off to the API's OpenID Connect sign-in. */
  onMicrosoftSignIn(): void {
    this.errorMessage = null;
    this.isRedirectingToSso = true;
    this._authSession.startSsoLogin('/');
  }

  onForgotPassword(): void {
    this.router.navigate(['/non-auth/forgot-password']);
  }

  onCreateAccount(): void {
    this.router.navigate(['/non-auth/create-account']);
  }
}
